import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { del, get, post } from "../api";
import { BarList, QualityBadge, Sparkline } from "../components/Charts";
import type { Point } from "../components/Charts";
import { IntegrationGuide } from "../components/IntegrationGuide";
import { useI18n } from "../i18n";

interface Quality {
  [k: string]: unknown;
  coverage: string; never_collected?: boolean; pending_aggregation: number;
  processed_through: string | null; provisional: boolean;
  collection_scope?: { mode: string; channels: Record<string, boolean> };
  agent_health?: { source_kind: string; state: string; reported_at: string }[];
  known_gaps?: { count: number; latest_end: string | null };
  versions?: Record<string, number>;
  note?: string;
}

interface QueryResp {
  metric: string; granularity: string; quality: Quality;
  rule_version?: number; rule_version_effective?: number;
  data_scope?: string;
  series?: (Point & { uv?: number })[]; total?: number; total_uv?: number;
  items?: { value: string; count: number }[]; has_overflow?: boolean; total_in_window?: number;
  metrics?: Record<string, {
    sample_count: number; p75: string | null; approximate: boolean;
    grades?: Record<string, number>; low_sample?: boolean; note?: string | null;
  }>;
  conversion_rate?: number | null; qualified_pv?: number; converted_pages?: number;
  note?: string | null;
}

function formatCountryName(code: string, locale: "en" | "zh", unknownLabel: string): string {
  if (!code || code === "__unknown__") return unknownLabel;
  const regionNames = typeof Intl !== "undefined" && (Intl as any).DisplayNames
    ? new (Intl as any).DisplayNames([locale === "zh" ? "zh-CN" : "en"], { type: "region" })
    : null;
  if (regionNames && /^[A-Z]{2}$/.test(code)) {
    try {
      const name = regionNames.of(code);
      if (name) return `${code} (${name})`;
    } catch {
      // fallback
    }
  }
  return code;
}

function getTimeWindow(range: "24h" | "today" | "7d" | "30d"): { start: string; end: string; granularity?: string } {
  const now = new Date();
  const end = new Date(now.getTime() + 5 * 60_000);
  if (range === "24h") {
    const start = new Date(end.getTime() - 24 * 3600_000);
    return { start: start.toISOString(), end: end.toISOString(), granularity: "hour" };
  }
  if (range === "today") {
    const start = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0);
    return { start: start.toISOString(), end: end.toISOString(), granularity: "hour" };
  }
  if (range === "7d") {
    const start = new Date(end.getTime() - 7 * 86400_000);
    return { start: start.toISOString(), end: end.toISOString(), granularity: "day" };
  }
  const start = new Date(end.getTime() - 30 * 86400_000);
  return { start: start.toISOString(), end: end.toISOString(), granularity: "day" };
}

function CountryBarList({ items, overflow, locale, unknownLabel, emptyLabel }: {
  items: { value: string; count: number }[]; overflow?: boolean; locale: "en" | "zh";
  unknownLabel: string; emptyLabel: string;
}) {
  if (!items || items.length === 0) return <div className="empty-chart">{emptyLabel}</div>;
  const max = Math.max(...items.map((i) => i.count), 1);
  return (
    <div className="barlist">
      {overflow && (
        <div className="badge warn" style={{ marginBottom: 8 }}>
          {locale === "zh" ? "存在 __other_overflow__ 降级分组: 榜单非全量精确 Top K" : "Includes __other_overflow__ fallback group: Non-exact Top K"}
        </div>
      )}
      {items.map((i) => (
        <div className="barrow" key={i.value}>
          <span className="barlabel" title={i.value}>{formatCountryName(i.value, locale, unknownLabel)}</span>
          <div className="bartrack">
            <div className="barfill" style={{ width: `${(i.count / max) * 100}%`, backgroundColor: "#0284c7" }} />
          </div>
          <span className="barcount">{i.count.toLocaleString()}</span>
        </div>
      ))}
    </div>
  );
}

function QualityCard({ quality }: { quality: Quality }) {
  const { t } = useI18n();
  const channels = quality.collection_scope?.channels;
  const health = quality.agent_health ?? [];
  return (
    <div className="card" style={{ marginBottom: 12 }}>
      <b>{t.siteDetail.qualityCoverage}</b>{" "}
      <span className="muted">
        coverage={quality.coverage}
        {quality.never_collected && ` ${t.siteDetail.qualityNeverCollected}`}
        {typeof quality.versions?.rule === "number" &&
          ` · ${t.siteDetail.ruleVersion}${quality.versions.rule}`}
        {" · "}
        {t.siteDetail.pendingAggregation}{quality.pending_aggregation}
        {quality.processed_through &&
          ` · ${t.siteDetail.watermark}${new Date(quality.processed_through).toLocaleTimeString()}`}
        {quality.provisional && ` · ${t.siteDetail.provisional}`}
      </span>
      {channels && (
        <p className="muted" style={{ margin: "6px 0 0" }}>
          {t.siteDetail.collectionMode}{quality.collection_scope?.mode};
          {" "}{t.siteDetail.channelsLabel}origin={String(channels.origin)} / browser={String(channels.browser)}
          {health.length > 0 &&
            ` · ${t.siteDetail.agentStatus}${health.map((h) => `${h.source_kind}=${h.state}`).join(", ")}`}
          {quality.known_gaps && quality.known_gaps.count > 0 &&
            ` · ${t.siteDetail.knownGaps}${quality.known_gaps.count} (${quality.known_gaps.latest_end ?? "?"})`}
        </p>
      )}
    </div>
  );
}

export function SiteDetailPage() {
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  const { workspaceId, siteId } =
    useParams<{ workspaceId: string; siteId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const currentTab = searchParams.get("tab") === "sources" ? "sources" : "analytics";
  const setMainTab = (tab: "analytics" | "sources") => {
    if (tab === "sources") {
      setSearchParams({ tab: "sources" });
    } else {
      setSearchParams({});
    }
  };
  const [site, setSite] = useState<{ name: string; mode: string } | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteErr, setDeleteErr] = useState<string | null>(null);
  const [timeRange, setTimeRange] = useState<"24h" | "today" | "7d" | "30d">("24h");
  const [browser, setBrowser] = useState<QueryResp | null>(null);
  const [origin, setOrigin] = useState<QueryResp | null>(null);
  const [paths, setPaths] = useState<QueryResp | null>(null);
  const [referrers, setReferrers] = useState<QueryResp | null>(null);
  const [countries, setCountries] = useState<QueryResp | null>(null);
  const [rum, setRum] = useState<QueryResp | null>(null);
  const [conv, setConv] = useState<QueryResp | null>(null);
  const [html, setHtml] = useState<QueryResp | null>(null);
  const [browsers, setBrowsers] = useState<QueryResp | null>(null);
  const [devices, setDevices] = useState<QueryResp | null>(null);
  const [channels, setChannels] = useState<QueryResp | null>(null);
  const [aiSources, setAiSources] = useState<QueryResp | null>(null);
  const [entryPages, setEntryPages] = useState<QueryResp | null>(null);
  const [botSummary, setBotSummary] = useState<{
    total_bot_requests: number;
    bots: { name: string; count: number }[];
    top_crawled_pages: { path: string; count: number }[];
  } | null>(null);
  const [sessions, setSessions] = useState<{
    total_sessions: number;
    unique_visitors: number;
    bounced_sessions: number;
    bounce_rate: number;
    avg_pages_per_session: number;
    avg_session_duration_seconds: number;
  } | null>(null);
  const [realtime, setRealtime] = useState<{
    active_visitors: number;
    recent_pageviews: { path: string; occurred_at: string; country?: string }[];
  } | null>(null);
  const [err, setErr] = useState<string | null>(null);

  // Export modal state
  const [showExport, setShowExport] = useState(false);
  const [exportFormat, setExportFormat] = useState<"csv" | "json">("csv");
  const [exportKind, setExportKind] = useState<"events" | "metrics">("events");
  const [exportBusy, setExportBusy] = useState(false);
  const [exportResult, setExportResult] = useState<{
    job_id: string; status: string; download_token: string; expires_at: string;
  } | null>(null);
  const [exportErr, setExportErr] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!workspaceId || !siteId) return;
    const win = getTimeWindow(timeRange);
    const q = (metric: string, extra: Record<string, unknown> = {}) =>
      post<QueryResp>(`/api/v1/sites/${siteId}/query`,
        { metric, start: win.start, end: win.end, ...(win.granularity ? { granularity: win.granularity } : {}), ...extra });
    try {
      const sites = await get<{ items: { id: string; name: string; mode: string }[] }>(
        `/api/v1/workspaces/${workspaceId}/sites`);
      const curSite = sites.items.find((s) => s.id === siteId) ?? null;
      setSite(curSite);

      const b = await q("events_total", { source_kind: "browser" }).catch(() => null);
      if (b) setBrowser(b);

      const o = await q("events_total", { source_kind: "origin" }).catch(() => null);
      if (o) setOrigin(o);

      const p = await q("top_paths", { source_kind: "origin" }).catch(() => null);
      if (p) setPaths(p);

      const rf = await q("top_referrers", { source_kind: "browser" }).catch(() => null);
      if (rf) setReferrers(rf);

      const c = await q("top_countries", { source_kind: "browser" }).catch(() => null);
      if (c) setCountries(c);

      const r = await q("rum_summary").catch(() => null);
      if (r) setRum(r);

      const cv = await q("conversion_summary").catch(() => null);
      if (cv) setConv(cv);

      const hd = await q("html_documents", { source_kind: "origin" }).catch(() => null);
      if (hd) setHtml(hd);

      const br = await q("top_browsers", { source_kind: "browser" }).catch(() => null);
      if (br) setBrowsers(br);

      const dv = await q("top_devices", { source_kind: "browser" }).catch(() => null);
      if (dv) setDevices(dv);

      const ch = await q("top_channels", { source_kind: "browser" }).catch(() => null);
      if (ch) setChannels(ch);

      const ai = await q("top_ai_sources", { source_kind: "browser" }).catch(() => null);
      if (ai) setAiSources(ai);

      const ep = await q("top_entry_pages").catch(() => null);
      if (ep) setEntryPages(ep);

      const bs = await q("bot_summary").catch(() => null);
      if (bs) setBotSummary(bs as any);

      const sess = await q("sessions_summary", { source_kind: "browser" }).catch(() => null);
      if (sess) setSessions(sess as any);

      const rt = await get<{ active_visitors: number; recent_pageviews: { path: string; occurred_at: string; country?: string }[] }>(
        `/api/v1/sites/${siteId}/realtime/visitors?stream=false`
      ).catch(() => null);
      if (rt) setRealtime(rt);

      setErr(null);
    } catch (e) {
      setErr((e as Error).message);
    }
  }, [workspaceId, siteId, timeRange]);

  useEffect(() => {
    load();
    const t = setInterval(load, 30_000);
    return () => clearInterval(t);
  }, [load]);

  async function handleDeleteSite() {
    setDeleteBusy(true);
    setDeleteErr(null);
    try {
      await del(`/api/v1/sites/${siteId}`);
      setShowDeleteModal(false);
      navigate(`/w/${workspaceId}`);
    } catch (e) {
      setDeleteErr((e as Error).message);
    } finally {
      setDeleteBusy(false);
    }
  }

  if (!site && err) {
    return (
      <div className="center card" style={{ maxWidth: 460, margin: "40px auto", textAlign: "center" }}>
        <p className="error" style={{ marginBottom: 16 }}>{err}</p>
        <button className="primary" onClick={() => load()}>{t.siteDetail.reload}</button>
      </div>
    );
  }
  if (!site) return <div className="muted">{t.common.loading}</div>;

  return (
    <>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, flexWrap: "wrap", gap: 12 }}>
        <h2>
          <Link to={`/w/${workspaceId}`}>←</Link> {site.name}
          <span className="muted" style={{ fontSize: 13, marginLeft: 8 }}>
            {t.siteDetail.collectionMode}{site.mode}; {t.siteDetail.modeNotice}
          </span>
        </h2>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          {currentTab === "analytics" ? (
            <>
              <div style={{ display: "inline-flex", background: "#e2e8f0", borderRadius: 8, padding: 3 }}>
                {(["24h", "today", "7d", "30d"] as const).map((r) => (
                  <button
                    key={r}
                    onClick={() => setTimeRange(r)}
                    style={{
                      padding: "4px 12px",
                      fontSize: 12,
                      border: "none",
                      borderRadius: 6,
                      cursor: "pointer",
                      background: timeRange === r ? "#2563eb" : "transparent",
                      color: timeRange === r ? "#fff" : "#475569",
                      fontWeight: timeRange === r ? 600 : 400,
                      transition: "all 0.15s ease",
                    }}
                  >
                    {r === "24h"
                      ? t.siteDetail.range24h
                      : r === "today"
                        ? t.siteDetail.rangeToday
                        : r === "7d"
                          ? t.siteDetail.range7d
                          : t.siteDetail.range30d}
                  </button>
                ))}
              </div>
              <button className="primary" onClick={() => {
                setShowExport(true);
                setExportResult(null);
                setExportErr(null);
              }}>
                {t.siteDetail.exportBtn}
              </button>
            </>
          ) : (
            <button className="secondary" onClick={() => setMainTab("analytics")}>
              {locale === "zh" ? "← 返回数据分析看板" : "← Back to Analytics"}
            </button>
          )}
        </div>
      </div>

      {/* Top-Level Navigation Tabs */}
      <div style={{ display: "flex", gap: 12, borderBottom: "2px solid #e2e8f0", marginBottom: 20 }}>
        <button
          onClick={() => setMainTab("analytics")}
          style={{
            padding: "10px 20px",
            fontSize: 15,
            fontWeight: 600,
            border: "none",
            background: "transparent",
            cursor: "pointer",
            borderBottom: currentTab === "analytics" ? "3px solid #2563eb" : "3px solid transparent",
            color: currentTab === "analytics" ? "#2563eb" : "#64748b",
            marginBottom: -2,
            transition: "all 0.15s ease",
          }}
        >
          {t.siteDetail.analyticsTab}
        </button>
        <button
          onClick={() => setMainTab("sources")}
          style={{
            padding: "10px 20px",
            fontSize: 15,
            fontWeight: 600,
            border: "none",
            background: "transparent",
            cursor: "pointer",
            borderBottom: currentTab === "sources" ? "3px solid #2563eb" : "3px solid transparent",
            color: currentTab === "sources" ? "#2563eb" : "#64748b",
            marginBottom: -2,
            transition: "all 0.15s ease",
          }}
        >
          {t.siteDetail.sourcesTab}
        </button>
      </div>

      {currentTab === "sources" ? (
        <div style={{ marginTop: 12 }}>
          {siteId && workspaceId && (
            <IntegrationGuide
              siteId={siteId}
              siteName={site.name}
              workspaceId={workspaceId}
            />
          )}
        </div>
      ) : (
        <>
          {/* Quick jump banner to sources */}
          <div className="card" style={{ marginBottom: 16, background: "#f8fafc", border: "1px dashed #cbd5e1", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12, padding: "12px 18px" }}>
            <div>
              <span style={{ fontWeight: 600, color: "#1e293b", fontSize: 14 }}>
                🔌 {locale === "zh" ? "数据采集与来源配置 (Sources)" : "Data Sources & Ingestion Setup"}
              </span>
              <p className="muted" style={{ margin: "4px 0 0", fontSize: 13 }}>
                {locale === "zh"
                  ? "查看与签发浏览器探针 (pub_key)、服务端 Agent Key，或浏览 Nginx / Node.js 等接入代码。"
                  : "Manage browser public key, origin Agent Key, and view full integration guides."}
              </p>
            </div>
            <button className="secondary" onClick={() => setMainTab("sources")} style={{ whiteSpace: "nowrap", fontSize: 13 }}>
              {locale === "zh" ? "打开接入与密钥管理 →" : "Open Sources & Keys →"}
            </button>
          </div>

      {err && (
        <div style={{
          backgroundColor: "#fef2f2",
          border: "1px solid #f87171",
          color: "#b91c1c",
          padding: "10px 14px",
          borderRadius: 8,
          marginBottom: 16,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}>
          <span>{err}</span>
          <button className="ghost" style={{ fontSize: 12, padding: "2px 8px", color: "#b91c1c" }} onClick={() => load()}>
            {t.siteDetail.retry}
          </button>
        </div>
      )}

      <QualityCard quality={browser?.quality ?? origin?.quality ?? {
        coverage: "unknown", pending_aggregation: 0, processed_through: null,
        provisional: false,
      }} />

      <div className="grid2" style={{ marginBottom: 16 }}>
        <div className="card">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <h3 style={{ margin: 0 }}>{t.siteDetail.realtimeLiveTitle}</h3>
            <span style={{ fontSize: 12, background: "#10b981", color: "#fff", padding: "2px 8px", borderRadius: 12, fontWeight: "bold" }}>
              {t.siteDetail.realtimeLiveBadge}
            </span>
          </div>
          <p style={{ fontSize: 32, fontWeight: "bold", margin: "12px 0 6px", color: "#10b981" }}>
            {realtime?.active_visitors ?? 0} <span style={{ fontSize: 14, color: "#64748b", fontWeight: "normal" }}>{t.siteDetail.activeVisitorsCount}</span>
          </p>
          <div style={{ maxHeight: 90, overflowY: "auto", fontSize: 12 }}>
            {(realtime?.recent_pageviews ?? []).length === 0 ? (
              <p className="muted" style={{ margin: 0 }}>{t.siteDetail.noRecentVisits}</p>
            ) : (
              (realtime?.recent_pageviews ?? []).slice(0, 4).map((pv, i) => (
                <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "2px 0", borderBottom: "1px dashed #e2e8f0" }}>
                  <span style={{ fontFamily: "monospace" }}>{pv.path}</span>
                  <span className="muted">{new Date(pv.occurred_at).toLocaleTimeString()}</span>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="card">
          <h3 style={{ margin: 0 }}>{t.siteDetail.sessionsCardTitle}</h3>
          {sessions ? (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginTop: 8 }}>
              <div>
                <span className="muted" style={{ fontSize: 12 }}>{t.siteDetail.totalSessions}</span>
                <p style={{ fontSize: 20, fontWeight: "bold", margin: "2px 0" }}>{sessions.total_sessions}</p>
              </div>
              <div>
                <span className="muted" style={{ fontSize: 12 }}>{t.siteDetail.uniqueVisitors}</span>
                <p style={{ fontSize: 20, fontWeight: "bold", margin: "2px 0", color: "#2563eb" }}>{sessions.unique_visitors ?? browser?.total_uv ?? "—"}</p>
              </div>
              <div>
                <span className="muted" style={{ fontSize: 12 }}>{t.siteDetail.bounceRate}</span>
                <p style={{ fontSize: 20, fontWeight: "bold", margin: "2px 0", color: sessions.bounce_rate > 0.5 ? "#ef4444" : "#10b981" }}>
                  {(sessions.bounce_rate * 100).toFixed(1)}%
                </p>
              </div>
              <div>
                <span className="muted" style={{ fontSize: 12 }}>{t.siteDetail.avgPages}</span>
                <p style={{ fontSize: 16, fontWeight: "600", margin: "2px 0" }}>{sessions.avg_pages_per_session} {t.siteDetail.pagesUnit}</p>
              </div>
              <div style={{ gridColumn: "span 2" }}>
                <span className="muted" style={{ fontSize: 12 }}>{t.siteDetail.avgDuration}</span>
                <p style={{ fontSize: 16, fontWeight: "600", margin: "2px 0" }}>{Math.round(sessions.avg_session_duration_seconds)} {t.siteDetail.secondsUnit}</p>
              </div>
            </div>
          ) : (
            <p className="muted" style={{ marginTop: 12 }}>{t.siteDetail.noSessionData}</p>
          )}
          <p className="muted" style={{ fontSize: 11, margin: "8px 0 0" }}>{t.siteDetail.sessionSplitNote}</p>
        </div>
      </div>

      <div className="grid2">
        <div className="card">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <h3 style={{ margin: 0 }}>{t.siteDetail.browserTrafficTitle}</h3>
            <span className="badge" style={{ fontSize: 11, background: "#ede9fe", color: "#6d28d9" }}>
              {timeRange === "24h" ? t.siteDetail.range24h : timeRange === "today" ? t.siteDetail.rangeToday : timeRange === "7d" ? t.siteDetail.range7d : t.siteDetail.range30d}
            </span>
          </div>
          {browser && <>
            <div style={{ display: "flex", gap: 16, margin: "10px 0 6px" }}>
              <div>
                <span className="muted" style={{ fontSize: 12 }}>{t.siteDetail.totalPv}</span>
                <p style={{ fontSize: 22, fontWeight: "bold", margin: "2px 0", color: "#7c3aed" }}>
                  {browser.total?.toLocaleString() ?? 0}
                </p>
              </div>
              <div>
                <span className="muted" style={{ fontSize: 12 }}>{t.siteDetail.uniqueVisitors}</span>
                <p style={{ fontSize: 22, fontWeight: "bold", margin: "2px 0", color: "#2563eb" }}>
                  {browser.total_uv ?? (sessions?.unique_visitors ?? "—")}
                </p>
              </div>
              <div>
                <span className="muted" style={{ fontSize: 12 }}>{t.siteDetail.pvUvRatio}</span>
                <p style={{ fontSize: 22, fontWeight: "bold", margin: "2px 0", color: "#059669" }}>
                  {(browser.total && (browser.total_uv || sessions?.unique_visitors))
                    ? (browser.total / (browser.total_uv || sessions?.unique_visitors || 1)).toFixed(2)
                    : "—"}
                </p>
              </div>
            </div>
            <QualityBadge quality={browser.quality} />
            <Sparkline points={browser.series ?? []} color="#7c3aed" />
            <p className="muted" style={{ fontSize: 12, marginTop: 6 }}>
              {browser.total?.toLocaleString() ?? 0} PV · {browser.total_uv != null ? `${browser.total_uv} UV · ` : ""}
              {browser.granularity} · {browser.data_scope}
            </p>
          </>}
        </div>
        <div className="card">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <h3 style={{ margin: 0 }}>{t.siteDetail.originRequestsTitle}</h3>
            <span className="badge" style={{ fontSize: 11, background: "#f1f5f9", color: "#475569" }}>
              {timeRange === "24h" ? t.siteDetail.range24h : timeRange === "today" ? t.siteDetail.rangeToday : timeRange === "7d" ? t.siteDetail.range7d : t.siteDetail.range30d}
            </span>
          </div>
          {origin && <>
            <div style={{ margin: "10px 0 6px" }}>
              <span className="muted" style={{ fontSize: 12 }}>{t.siteDetail.totalOriginRequests}</span>
              <p style={{ fontSize: 22, fontWeight: "bold", margin: "2px 0" }}>
                {origin.total?.toLocaleString() ?? 0}
              </p>
            </div>
            <QualityBadge quality={origin.quality} />
            <Sparkline points={origin.series ?? []} />
            <p className="muted" style={{ fontSize: 12, marginTop: 6 }}>
              {origin.total?.toLocaleString() ?? 0} · {t.siteDetail.htmlDocsNotice} {html?.total?.toLocaleString() ?? 0} · {t.siteDetail.originNotice}
            </p>
          </>}
        </div>
      </div>

      <div className="grid2">
        <div className="card">
          <h3>{t.siteDetail.topCountriesTitle}</h3>
          {countries && (
            <CountryBarList
              items={countries.items ?? []}
              overflow={countries.has_overflow}
              locale={locale}
              unknownLabel={t.siteDetail.unknownLocation}
              emptyLabel={t.siteDetail.emptyChart}
            />
          )}
          <p className="muted">{t.siteDetail.topCountriesNote}</p>
        </div>
        <div className="card">
          <h3>{t.siteDetail.topReferrersTitle}</h3>
          {referrers && <BarList items={referrers.items ?? []} overflow={referrers.has_overflow} />}
          <p className="muted">{t.siteDetail.topReferrersNote}</p>
        </div>
      </div>

      <div className="grid2">
        <div className="card">
          <h3>{t.siteDetail.topPathsTitle}</h3>
          {paths && <BarList items={paths.items ?? []} overflow={paths.has_overflow} />}
          {paths?.note && <p className="muted">{paths.note}</p>}
        </div>
        <div className="card">
          <h3>{t.siteDetail.topLandingPagesTitle}</h3>
          {entryPages && <BarList items={entryPages.items ?? []} overflow={entryPages.has_overflow} />}
          <p className="muted">{t.siteDetail.landingPagesNote}</p>
        </div>
      </div>

      <div className="grid2">
        <div className="card">
          <h3>{t.siteDetail.trafficChannelsTitle}</h3>
          {channels && <BarList items={channels.items ?? []} overflow={channels.has_overflow} />}
          <p className="muted">{t.siteDetail.channelsNote}</p>
        </div>
        <div className="card">
          <h3>{t.siteDetail.aiTrafficTitle}</h3>
          {aiSources && <BarList items={aiSources.items ?? []} overflow={aiSources.has_overflow} />}
          <p className="muted">{t.siteDetail.aiTrafficNote}</p>
        </div>
      </div>

      <div className="grid2">
        <div className="card">
          <h3>{t.siteDetail.devicesTitle}</h3>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div>
              <span className="muted" style={{ fontSize: 12, fontWeight: 600 }}>{t.siteDetail.deviceType}</span>
              {devices && <BarList items={devices.items ?? []} overflow={devices.has_overflow} />}
            </div>
            <div>
              <span className="muted" style={{ fontSize: 12, fontWeight: 600 }}>{t.siteDetail.browserFamily}</span>
              {browsers && <BarList items={browsers.items ?? []} overflow={browsers.has_overflow} />}
            </div>
          </div>
          <p className="muted" style={{ marginTop: 8 }}>{t.siteDetail.devicesNote}</p>
        </div>
        <div className="card">
          <h3>{t.siteDetail.botsTitle}</h3>
          {botSummary ? (
            <div>
              <p style={{ margin: "4px 0 10px" }}>
                <b>{t.siteDetail.totalBotRequests}</b>
                <span className="badge warn" style={{ fontSize: 13 }}>
                  {botSummary.total_bot_requests.toLocaleString()}
                </span>
              </p>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <div>
                  <span className="muted" style={{ fontSize: 12, fontWeight: 600 }}>{t.siteDetail.topBots}</span>
                  <BarList items={botSummary.bots.map(b => ({ value: b.name, count: b.count }))} />
                </div>
                <div>
                  <span className="muted" style={{ fontSize: 12, fontWeight: 600 }}>{t.siteDetail.topCrawledPages}</span>
                  <BarList items={botSummary.top_crawled_pages.map(p => ({ value: p.path, count: p.count }))} />
                </div>
              </div>
            </div>
          ) : (
            <div className="empty-chart">{t.siteDetail.noBotData}</div>
          )}
          <p className="muted" style={{ marginTop: 8 }}>{t.siteDetail.botsNote}</p>
        </div>
      </div>

      <div className="grid2">
        <div className="card">
          <h3>{t.siteDetail.rumTitle}</h3>
          {rum?.metrics && Object.entries(rum.metrics).map(([name, m]) => (
            <div key={name} style={{ marginBottom: 10 }}>
              <b>{name}</b>{" "}
              {m.sample_count === 0
                ? <span className="muted">{t.siteDetail.rumNoSamples}</span>
                : <>
                    <span>p75 ≈ {m.p75}</span>{" "}
                    <span className="muted">
                      {locale === "zh" ? `样本 ${m.sample_count}` : `samples ${m.sample_count}`}
                      {m.low_sample && ` · ${t.siteDetail.rumLowSample}`}
                      {m.grades && ` · ${t.siteDetail.rumGood} ${m.grades.good} / ${t.siteDetail.rumNeedsImprovement} ${m.grades.needs_improvement} / ${t.siteDetail.rumPoor} ${m.grades.poor}`}
                    </span>
                  </>}
            </div>
          ))}
          <p className="muted">{t.siteDetail.rumNote}</p>
        </div>
        <div className="card">
          <h3>{t.siteDetail.conversionsTitle}</h3>
          {conv && <>
            <p>
              <b>{conv.conversion_rate == null
                ? t.siteDetail.noQualifiedPv
                : `${(conv.conversion_rate * 100).toFixed(1)}%`}</b>
              <span className="muted">
                {" "}({conv.converted_pages ?? 0}/{conv.qualified_pv ?? 0} {locale === "zh" ? "页面" : "pages"})
              </span>
            </p>
            <p className="muted">{conv.note}</p>
          </>}
        </div>
      </div>


      {/* Export Modal */}
      {showExport && (
        <div style={{
          position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: "rgba(0,0,0,0.5)", display: "flex",
          alignItems: "center", justifyContent: "center", zIndex: 1000
        }}>
          <div className="card" style={{ maxWidth: 460, width: "90%", background: "#fff" }}>
            <h3>{t.siteDetail.exportModalTitle}</h3>
            {exportErr && <p className="error">{exportErr}</p>}
            
            {!exportResult ? (
              <>
                <label>{t.siteDetail.exportDataType}</label>
                <select value={exportKind} onChange={(e) => setExportKind(e.target.value as "events" | "metrics")}
                        style={{ width: "100%", padding: 8, marginBottom: 12 }}>
                  <option value="events">{t.siteDetail.exportOptionEvents}</option>
                  <option value="metrics">{t.siteDetail.exportOptionMetrics}</option>
                </select>

                <label>{t.siteDetail.exportFormatLabel}</label>
                <div className="row" style={{ marginBottom: 16 }}>
                  <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
                    <input type="radio" name="fmt" checked={exportFormat === "csv"} onChange={() => setExportFormat("csv")} />
                    CSV
                  </label>
                  <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", marginLeft: 16 }}>
                    <input type="radio" name="fmt" checked={exportFormat === "json"} onChange={() => setExportFormat("json")} />
                    JSON (UTF-8)
                  </label>
                </div>

                <p className="muted" style={{ fontSize: 12 }}>
                  {t.siteDetail.exportCsvSafeNotice}
                </p>

                <div style={{ marginTop: 16, display: "flex", gap: 8 }}>
                  <button className="primary" disabled={exportBusy} onClick={async () => {
                    setExportBusy(true); setExportErr(null);
                    try {
                      const res = await post<{
                        job_id: string; status: string; download_token: string; expires_at: string;
                      }>(`/api/v1/sites/${siteId}/export`, {
                        format: exportFormat,
                        kind: exportKind,
                      });
                      setExportResult(res);
                    } catch (e) {
                      setExportErr((e as Error).message);
                    } finally {
                      setExportBusy(false);
                    }
                  }}>
                    {exportBusy ? t.siteDetail.exportGenerating : t.siteDetail.exportStartBtn}
                  </button>
                  <button className="ghost" onClick={() => setShowExport(false)}>{t.common.cancel}</button>
                </div>
              </>
            ) : (
              <div>
                <p className="badge ok">{t.siteDetail.exportReady}</p>
                <p>{t.siteDetail.exportJobId}<code>{exportResult.job_id}</code></p>
                <p>{t.siteDetail.exportToken}<code>{exportResult.download_token}</code></p>
                <p className="muted" style={{ fontSize: 12 }}>{t.siteDetail.exportExpires}</p>
                
                <div style={{ marginTop: 16, display: "flex", gap: 8 }}>
                  <a href={`/api/v1/export/download/${exportResult.download_token}`} download
                     className="primary" style={{ display: "inline-block", padding: "8px 16px", borderRadius: 6, textDecoration: "none", color: "#fff", background: "#2563eb" }}>
                    {t.siteDetail.downloadFileBtn} ({exportFormat.toUpperCase()})
                  </a>
                  <button className="ghost" onClick={() => setShowExport(false)}>{t.siteDetail.closeBtn}</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Danger Zone */}
      <div className="card" style={{ borderColor: "#fca5a5", marginTop: 24 }}>
        <h3 style={{ color: "#b91c1c" }}>{t.siteDetail.dangerZoneTitle}</h3>
        <p className="muted">{t.siteDetail.deleteSiteNotice}</p>
        <button
          style={{ background: "#dc2626", color: "#fff", border: "none", padding: "8px 16px", borderRadius: 6, cursor: "pointer" }}
          onClick={() => {
            setShowDeleteModal(true);
            setDeleteErr(null);
          }}
        >
          {t.siteDetail.deleteSiteBtn}
        </button>
      </div>
      </>
      )}

      {/* Delete Site Modal */}
      {showDeleteModal && (
        <div style={{
          position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: "rgba(0,0,0,0.5)", display: "flex",
          alignItems: "center", justifyContent: "center", zIndex: 1000
        }}>
          <div className="card" style={{ maxWidth: 460, width: "90%", background: "#fff" }}>
            <h3 style={{ color: "#b91c1c" }}>{t.siteDetail.deleteSiteConfirmTitle}: {site?.name}</h3>
            <p className="muted">{t.siteDetail.deleteSiteConfirmPrompt}</p>
            {deleteErr && <p className="error">{deleteErr}</p>}
            <div style={{ marginTop: 16, display: "flex", gap: 8 }}>
              <button
                style={{ background: "#dc2626", color: "#fff", border: "none", padding: "8px 16px", borderRadius: 6, cursor: "pointer" }}
                disabled={deleteBusy}
                onClick={handleDeleteSite}
              >
                {deleteBusy ? t.common.submitting : t.common.delete}
              </button>
              <button className="ghost" onClick={() => setShowDeleteModal(false)}>{t.common.cancel}</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
